import React, { FC, memo, useContext } from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';

import styles, { BASE_FONT_SIZE } from '../pdfStyles';
import PdfLogoComponent from '../PdfComponents/PdfLogo';
import PdfFooter from '../PdfComponents/PdfFooter';
import PdfHeaderDetails from '../PdfComponents/PdfHeaderDetails';
import PdfMainDetails from '../PdfComponents/PdfMainDetails';
import PdfTitleTextBox from '../PdfComponents/PdfTitleTextBox';
import { PdfRemarksSection } from '../PdfComponents/PdfSection';
import PdfPassengerDetails from '../PdfComponents/PdfPassengerDetails';
import PdfTableComponent from '../PdfComponents/PdfTable';
import PdfComment from '../PdfComponents/PdfComment';
import PdfMultipleTextBox from '../PdfComponents/PdfMultipleTextBox';
import PdfTotalBox from '../PdfComponents/PdfTotalBox';
import PdfBillingContactDetails from '../PdfComponents/PdfBillingContactDetails';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Client } from '../../../api/clients/types';
import { VouchersPdfPropsType } from './voucherHelpers';

const InvoicePdf: FC<VouchersPdfPropsType> = (props: VouchersPdfPropsType) => {
    const clientsContext = useContext(ClientsContext);

    const {
        info: {
            dueDate,
            travelFileNumber,
            travelFileOpenDate,
            travelFileStartDate,
            remark,
            paidOnAccount,
            agentReference,
            pdfInfo: {
                companyInfo,
                userName,
                showIssuedBy,
                pdfImageSource = (clientsContext.selected as Client)?.companyLogoUrl,
                agentInfo,
                passenger,
                columns,
                rows,
                summaryLine,
                rateTableColumns,
                rateTableRows,
                rateLine,
                bottomDetails,
                topLeftDetailsText,
                pdfTotalBoxData,
                voucherCertification = 'Draft',
                passengersTableColumns,
                passengersTableRows,
                equivalentAmountText,
                vatComment
            }
        },
        number: voucherNumber,
        type: voucherType,
        createdAt
    } = props;

    return (
        <Document title={`${voucherType} ${voucherNumber || ''}`}>
            <Page wrap size="A4" style={styles.page}>
                <PdfLogoComponent pdfImageSource={pdfImageSource} />

                <View style={styles.padding0_10}>
                    <View style={styles.section}>
                        <View style={{ ...styles.header, ...styles.spaceBetween }}>
                            <View>
                                {topLeftDetailsText ? <Text>{topLeftDetailsText}</Text> : <></>}
                                {passenger ? <PdfPassengerDetails passenger={passenger} withoutTo /> : <></>}
                                {agentInfo ? <PdfBillingContactDetails {...agentInfo} withTo /> : <></>}
                            </View>

                            <PdfHeaderDetails
                                companyVatNumber={companyInfo.vat}
                                iata={companyInfo.iata}
                                issueDate={props.issueDate || createdAt}
                                dueDate={dueDate}
                                travelFileNumber={travelFileNumber}
                                travelFileOpenDate={travelFileOpenDate}
                                travelFileStartDate={travelFileStartDate}
                            />
                        </View>
                    </View>
                    <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                        <Text style={{ ...styles.button }}>{voucherCertification}</Text>
                    </View>

                    <View style={{ ...styles.section, ...styles.alignCenter }} wrap={false}>
                        <PdfMainDetails
                            topDetails={`${voucherType} No. ${voucherNumber || 'No Document Number'}`}
                            bottomDetails={bottomDetails}
                            withForLabel
                        />
                    </View>

                    <View style={[styles.section, styles.marginBottom10px]}>
                        <PdfTableComponent columns={columns} rows={rows} summaryLine={summaryLine} />
                    </View>

                    {pdfTotalBoxData && <PdfTotalBox pdfTotalBoxData={pdfTotalBoxData} />}

                    {equivalentAmountText ? (
                        <View style={[styles.section, styles.paddingBottom10]}>
                            {equivalentAmountText ? <Text>{equivalentAmountText}</Text> : <></>}
                        </View>
                    ) : (
                        <></>
                    )}

                    {rateLine ? (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <Text style={[styles.littleText, styles.fontBold]}>{rateLine}</Text>
                        </View>
                    ) : rateTableRows?.length ? (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <PdfTableComponent
                                columns={rateTableColumns}
                                rows={rateTableRows}
                                tableStyle={styles.rateTableWidth}
                            />
                        </View>
                    ) : (
                        <></>
                    )}

                    {passengersTableRows ? (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <Text style={[styles.littleText, styles.marginBottom4px]}>Passenger list:</Text>
                            <PdfTableComponent columns={passengersTableColumns} rows={passengersTableRows} />
                        </View>
                    ) : (
                        <></>
                    )}

                    {agentReference ? (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <Text style={{ ...styles.littleText }}>Reference No. {agentReference}</Text>
                        </View>
                    ) : (
                        <></>
                    )}

                    <PdfComment comment={vatComment} />

                    <PdfRemarksSection body={remark} />

                    <PdfMultipleTextBox
                        borderStyle={styles.borderRed2px}
                        title="Paid on account"
                        pdfMultipleTextBoxData={paidOnAccount}
                    />

                    {showIssuedBy !== false && !!userName && <Text style={{ fontSize: BASE_FONT_SIZE - 1, color: '#6b7280', marginTop: 6, marginHorizontal: 10 }}>Issued by: {userName}</Text>}
                </View>

                <PdfFooter companyInfo={companyInfo} />
            </Page>
        </Document>
    );
};

export default memo(InvoicePdf);
